/**
 * useDashboardResource — one tiny fetch-state hook for the dashboard
 * surfaces (Lane D, 2026-07-09 wave).
 *
 * Every hero tile / page-level probe needs the same four things: data,
 * a loading flag, an error string, and a retry that refetches. Widgets
 * used to hand-roll this trio each; the hero composes five endpoints,
 * so the boilerplate earned a hook. Refetches when `refreshKey` bumps
 * (the shell Quick Book signal + local mutations share it — spec C3).
 *
 * Stale-while-revalidate: `data` is kept during a refetch so the hero
 * numbers don't blank on every check-in (`isLoading` only true before
 * the FIRST payload). Errors during a background refresh surface via
 * `error` while the last-good data stays rendered.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface DashboardResource<T> {
  data: T | null;
  /** True only while loading with no payload yet (skeleton state). */
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

export function useDashboardResource<T>(
  loader: () => Promise<T>,
  refreshKey: number,
  enabled = true,
): DashboardResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [isFetching, setIsFetching] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // The loader identity changes every render (inline closures at the
  // call sites); a ref keeps the effect keyed on refreshKey/attempt only.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setIsFetching(true);
    setError(null);
    void loaderRef
      .current()
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (cancelled) return;
        setIsFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, attempt, enabled]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { data, isLoading: isFetching && data === null, error, retry };
}
